import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { getAllDebts } from '../services/debtService';
import { getAllSubscriptions } from '../services/subscriptionService';
import type { Debt, DebtCategory } from '../types/debt';
import type { Subscription } from '../types/subscription';
import { BarChart2, Building, CheckCircle2, Clock, Target, Award, Calendar, AlertCircle, CreditCard } from 'lucide-react';
import { APP_CURRENCY } from '../config/currency';

const CATEGORY_COLORS: Record<DebtCategory, string> = {
  credit_card: '#818cf8',      // indigo
  personal_loan: '#34d399',    // emerald
  buy_now_pay_later: '#f59e0b',// amber
  mortgage: '#f87171',         // rose
  auto_loan: '#67e8f9',        // cyan
  other: '#a78bfa',            // violet
};

const CATEGORY_LABELS: Record<DebtCategory, string> = {
  credit_card: 'Credit Card',
  personal_loan: 'Personal Loan',
  buy_now_pay_later: 'Buy Now Pay Later',
  mortgage: 'Mortgage',
  auto_loan: 'Auto Loan',
  other: 'Other',
};

const Analytics = () => {
  const [debts, setDebts] = useState<Debt[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      const [debtData, subData] = await Promise.all([
        getAllDebts(),
        getAllSubscriptions()
      ]);
      setDebts(debtData);
      setSubscriptions(subData);
    };
    fetchData();
  }, []);

  const active = debts.filter((d) => d.status !== 'paid');
  const paid = debts.filter((d) => d.status === 'paid');

  // Category breakdown
  const catTotals: Partial<Record<DebtCategory, number>> = {};
  for (const d of active) {
    catTotals[d.category] = (catTotals[d.category] || 0) + (d.amount - d.amountPaid);
  }
  const sortedCats = Object.entries(catTotals).sort(([, a], [, b]) => b - a) as [DebtCategory, number][];
  const maxCat = sortedCats[0]?.[1] || 1;

  // Top creditors
  const creditorTotals: Record<string, number> = {};
  for (const d of active) {
    if (!creditorTotals[d.creditorName]) creditorTotals[d.creditorName] = 0;
    creditorTotals[d.creditorName] += (d.amount - d.amountPaid);
  }
  const topCreditors = Object.entries(creditorTotals)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);

  // New Insight Metrics
  const totalDebt = debts.reduce((sum, d) => sum + d.amount, 0);
  const totalPaid = debts.reduce((sum, d) => sum + d.amountPaid, 0);
  const progressPercent = totalDebt > 0 ? (totalPaid / totalDebt) * 100 : 0;

  const largestActiveDebt = active.length > 0
    ? active.reduce((max, d) => (d.amount - d.amountPaid) > (max.amount - max.amountPaid) ? d : max, active[0])
    : null;

  const now = new Date().toISOString();
  const upcomingDebts = active
    .filter(d => d.dueDate && d.dueDate > now)
    .sort((a, b) => a.dueDate!.localeCompare(b.dueDate!));
  const nextUp = upcomingDebts.length > 0 ? upcomingDebts[0] : null;

  const activeSubscriptions = subscriptions.filter(s => s.status === 'active' && s.nextBillingDate && s.nextBillingDate > now);
  const upcomingSubscriptions = activeSubscriptions
    .sort((a, b) => a.nextBillingDate!.localeCompare(b.nextBillingDate!))
    .slice(0, 5); // top 5 upcoming

  // Calendar rendering basics
  const todayDate = new Date();
  const currentMonthIdx = todayDate.getMonth();
  const currentYear = todayDate.getFullYear();
  const daysInMonth = new Date(currentYear, currentMonthIdx + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonthIdx, 1).getDay();
  const emptyDailySlots = Array(firstDayOfMonth).fill(null);
  const monthDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const formattedMonthName = todayDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const chartBarClass = "h-full rounded-full transition-all duration-700";

  return (
    <div className="min-h-screen flex" style={{ background: '#0f0f13' }}>
      <Sidebar />
      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="p-6 max-w-7xl mx-auto">

          <div className="mb-8 fade-in-up">
            <div className="flex items-center gap-2 mb-1">
              <BarChart2 className="h-5 w-5 text-indigo-400" />
              <h1 className="text-2xl font-bold text-white">Analytics</h1>
            </div>
            <p className="text-sm text-gray-500">A high-level view of your debt activity</p>
          </div>

          {/* Overall Progress */}
          {debts.length > 0 && (
            <div className="glass-card rounded-2xl p-6 mb-8 fade-in-up delay-75">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-white">Total Debt Progress</h2>
                  <p className="text-xs text-gray-400 mt-1">Overall repayment progress across all accounts</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{APP_CURRENCY} {totalPaid.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} <span className="text-sm font-normal text-gray-500">/ {totalDebt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></p>
                  <p className="text-xs text-emerald-400 font-medium">{progressPercent.toFixed(1)}% Paid Off</p>
                </div>
              </div>
              <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all duration-1000" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          )}

          {/* Actionable Insights */}
          {debts.length > 0 && (
            <div className="mb-8 fade-in-up delay-100">
              <h2 className="text-sm font-semibold text-white mb-4">Meaningful Insights</h2>
              <div className="grid md:grid-cols-3 gap-4">

                {/* Milestone Insight */}
                <div className="glass-card rounded-xl p-5 border-l-4 border-emerald-400">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-emerald-400/10 rounded-lg text-emerald-400"><Award className="w-4 h-4" /></div>
                    <h3 className="text-sm font-medium text-white">Milestone</h3>
                  </div>
                  <p className="text-sm text-gray-400 transition-colors">
                    {progressPercent >= 100 ? "Congratulations! You are completely debt-free!" :
                      progressPercent >= 50 ? `Great job! You've paid off over half of your debt. Keep the momentum going!` :
                        progressPercent > 0 ? `You've made a start! Every payment brings you closer to financial freedom.` :
                          "Start making payments to see your progress grow."}
                  </p>
                </div>

                {/* Largest Balance Insight */}
                {largestActiveDebt ? (
                  <div className="glass-card rounded-xl p-5 border-l-4 border-amber-400">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-amber-400/10 rounded-lg text-amber-400"><AlertCircle className="w-4 h-4" /></div>
                      <h3 className="text-sm font-medium text-white">Largest Balance</h3>
                    </div>
                    <p className="text-sm text-gray-400 transition-colors">
                      Your largest active debt is <strong className="text-white">{largestActiveDebt.creditorName}</strong> with
                      <span className="text-amber-400 font-medium ml-1">{APP_CURRENCY} {(largestActiveDebt.amount - largestActiveDebt.amountPaid).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span> remaining. Consider allocating extra funds here.
                    </p>
                  </div>
                ) : (
                  <div className="glass-card rounded-xl p-5 border-l-4 border-amber-400">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-amber-400/10 rounded-lg text-amber-400"><AlertCircle className="w-4 h-4" /></div>
                      <h3 className="text-sm font-medium text-white">Clear Slate</h3>
                    </div>
                    <p className="text-sm text-gray-400 transition-colors">
                      You have no active debts with remaining balances. You're doing excellent!
                    </p>
                  </div>
                )}

                {/* Upcoming Due Date Insight */}
                {nextUp ? (
                  <div className="glass-card rounded-xl p-5 border-l-4 border-indigo-400">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-indigo-400/10 rounded-lg text-indigo-400"><Calendar className="w-4 h-4" /></div>
                      <h3 className="text-sm font-medium text-white">Upcoming Due Date</h3>
                    </div>
                    <p className="text-sm text-gray-400 transition-colors">
                      Your next payment for <strong className="text-white">{nextUp.creditorName}</strong> is due on
                      <span className="text-indigo-400 font-medium ml-1">{new Date(nextUp.dueDate!).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}</span>.
                    </p>
                  </div>
                ) : (
                  <div className="glass-card rounded-xl p-5 border-l-4 border-indigo-400">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 bg-indigo-400/10 rounded-lg text-indigo-400"><Target className="w-4 h-4" /></div>
                      <h3 className="text-sm font-medium text-white">No Upcoming Dates</h3>
                    </div>
                    <p className="text-sm text-gray-400 transition-colors">
                      You have no recorded upcoming due dates for your active debts. Add due dates to stay organized!
                    </p>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Summary row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 fade-in-up delay-200">
            {[
              { label: 'Total Accounts', value: String(debts.length), sub: 'All time', icon: BarChart2, color: 'text-indigo-400 bg-indigo-400/10' },
              { label: 'Active', value: String(active.length), sub: 'Pending / partial', icon: Clock, color: 'text-amber-400 bg-amber-400/10' },
              { label: 'Settled', value: String(paid.length), sub: 'Fully paid off', icon: CheckCircle2, color: 'text-emerald-400 bg-emerald-400/10' },
              { label: 'Unique Creditors', value: String(Object.keys(creditorTotals).length + paid.reduce((s, d) => { s.add(d.creditorName); return s; }, new Set<string>()).size), sub: 'Banks & Services', icon: Building, color: 'text-violet-400 bg-violet-400/10' },
            ].map((item) => (
              <div key={item.label} className="glass-card rounded-2xl p-4">
                <div className={`w-8 h-8 rounded-xl flex items-center justify-center mb-3 ${item.color}`}>
                  <item.icon className="h-4 w-4" />
                </div>
                <p className="text-2xl font-bold text-white">{item.value}</p>
                <p className="text-xs text-gray-500 mt-1">{item.label}</p>
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Category breakdown */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-300">
              <h2 className="text-sm font-semibold text-white mb-5">By Category <span className="text-gray-600 font-normal text-xs">(active only)</span></h2>
              {sortedCats.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No active debts to display.</p>
              ) : (
                <div className="space-y-4">
                  {sortedCats.map(([cat, total]) => (
                    <div key={cat}>
                      <div className="flex justify-between text-xs mb-1.5">
                        <span className="text-gray-400">{CATEGORY_LABELS[cat]}</span>
                        <span className="text-white font-medium">{APP_CURRENCY} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                      </div>
                      <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                        <div className={chartBarClass} style={{ width: `${(total / maxCat) * 100}%`, background: CATEGORY_COLORS[cat] }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top creditors */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-400">
              <h2 className="text-sm font-semibold text-white mb-5">Top Creditors <span className="text-gray-600 font-normal text-xs">(by active balance)</span></h2>
              {topCreditors.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No active creditors.</p>
              ) : (
                <div className="grid gap-3">
                  {topCreditors.map(([name, activeBalance]) => (
                    <div key={name} className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-xl bg-indigo-500/20 flex items-center justify-center text-xs font-bold text-indigo-400">
                          {name.substring(0, 2).toUpperCase()}
                        </div>
                        <span className="text-sm font-medium text-white">{name}</span>
                      </div>
                      <div className="text-right text-sm">
                        <p className="text-white font-medium">{APP_CURRENCY} {activeBalance.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Upcoming Subscriptions */}
            <div className="glass-card rounded-2xl p-6 fade-in-up delay-500">
              <div className="flex justify-between items-center mb-5">
                <h2 className="text-sm font-semibold text-white">Upcoming Subscriptions <span className="text-gray-600 font-normal text-xs block xl:inline mt-1 xl:mt-0">(next billing dates)</span></h2>
                <div className="text-xs font-medium px-2 py-1 bg-white/5 border border-white/5 rounded-full text-gray-300">
                  {formattedMonthName}
                </div>
              </div>

              {/* Mini Calendar View */}
              <div className="mb-6 bg-black/20 rounded-xl p-3 border border-white/5">
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
                    <div key={d} className="text-center text-[10px] uppercase font-bold text-gray-500 pt-1 pb-2">
                      {d}
                    </div>
                  ))}
                  {emptyDailySlots.map((_, i) => <div key={`empty-${i}`} className="p-1" />)}
                  {monthDays.map(day => {
                    const defaultDateStr = `${currentYear}-${String(currentMonthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const daySubs = activeSubscriptions.filter(s => s.nextBillingDate?.startsWith(defaultDateStr));
                    const isToday = day === todayDate.getDate();
                    const hasSub = daySubs.length > 0;

                    let bgClass = "text-gray-400 hover:bg-white/5 hover:text-white";
                    let markerColor = "";
                    if (isToday) {
                      bgClass = "bg-indigo-500 text-white font-bold shadow-md shadow-indigo-500/20";
                    } else if (hasSub) {
                      bgClass = "bg-emerald-500/10 text-emerald-400 font-bold border border-emerald-500/20";
                      markerColor = "bg-emerald-400";
                    }

                    return (
                      <div key={day} className={`group relative p-1 text-center text-xs rounded-lg transition-all flex flex-col items-center justify-center aspect-square ${hasSub ? 'cursor-pointer' : 'cursor-default'} ${bgClass}`}>
                        <span>{day}</span>
                        {hasSub && !isToday && (
                          <div className={`mt-0.5 w-[3px] h-[3px] rounded-full ${markerColor}`} />
                        )}
                        {hasSub && isToday && (
                          <div className="mt-0.5 w-[3px] h-[3px] rounded-full bg-white" />
                        )}

                        {/* Hover Tooltip */}
                        {hasSub && (
                          <div className="absolute z-10 bottom-full mb-1 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200">
                            <div className="w-max max-w-[140px] bg-[#1a1a24] border border-white/10 text-white rounded-lg shadow-2xl p-2.5">
                              <div className="flex flex-col gap-2 text-left">
                                {daySubs.map(sub => (
                                  <div key={sub.id} className="flex flex-col border-b border-white/5 last:border-0 pb-1.5 last:pb-0">
                                    <span className="font-semibold text-[11px] truncate" title={sub.customName || sub.serviceName}>
                                      {sub.customName || sub.serviceName}
                                    </span>
                                    <span className="text-emerald-400 text-[10px] font-medium whitespace-nowrap">
                                      {sub.currency} {sub.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                ))}
                              </div>
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-[#1a1a24]" />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {upcomingSubscriptions.length === 0 ? (
                <p className="text-sm text-gray-500 py-6 text-center">No upcoming subscriptions.</p>
              ) : (
                <div className="grid gap-3">
                  {upcomingSubscriptions.map((sub) => (
                    <div key={sub.id} className="flex items-center justify-between p-3 rounded-xl bg-white/3 border border-white/5 relative group cursor-pointer overflow-hidden hover:bg-white/5 transition-colors">
                      {/* Left indicator bar */}
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500/50 group-hover:bg-emerald-400 transition-colors" />

                      <div className="flex items-center gap-3 pl-2">
                        <div className="w-8 h-8 rounded-xl bg-emerald-500/10 flex items-center justify-center text-xs font-bold text-emerald-400 group-hover:scale-110 transition-transform">
                          <CreditCard className="w-4 h-4" />
                        </div>
                        <div>
                          <span className="block text-sm font-medium text-white line-clamp-1">{sub.customName || sub.serviceName}</span>
                          <span className="block text-xs text-gray-400">{new Date(sub.nextBillingDate!).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</span>
                        </div>
                      </div>
                      <div className="text-right text-sm overflow-hidden">
                        <p className="text-white font-medium whitespace-nowrap">{sub.currency} {sub.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
                        <p className="text-xs text-gray-500 capitalize truncate">{sub.billingCycle}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Analytics;
